import { useEffect, useMemo, useState } from 'react';
import { Check, Pencil, Plus, Route, Search } from 'lucide-react';
import {
  EmptyState, IconTile, Notice, PageHeader, SoftCard, field, pill,
} from '@/components/PageKit';
import { listenToTracks, saveTrack, type Track } from '@core/firebase/tracks';
import { filterSurahs } from '@core/quran/surahSearch';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';

type Draft = { id?: string; name: string; surahs: number[] };

/**
 * Where an admin builds the tracks students take.
 *
 * The سور are chosen through the same folded search the mushaf's picker uses,
 * so typing الكهف finds سُورَةُ الكَهۡفِ rather than nothing — the stored name
 * carries full diacritics and nobody types those into a search box.
 */
export default function AdminTracksPage() {
  const { t, language } = useLanguage();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [term, setTerm] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => listenToTracks(setTracks, () => setErrorKey('admin.tracks.failed')), []);

  const matches = useMemo(() => filterSurahs(term), [term]);
  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);

  const save = async () => {
    if (!editing || busy) return;
    // Refused here as well as on the server, so the admin is told which of the
    // two is missing rather than being handed an internal error.
    if (!editing.name.trim()) { setErrorKey('admin.tracks.needName'); return; }
    if (editing.surahs.length === 0) { setErrorKey('admin.tracks.needSurahs'); return; }
    setBusy(true);
    setErrorKey(null);
    try {
      await saveTrack({ id: editing.id, name: editing.name.trim(), surahs: editing.surahs });
      setEditing(null);
      setTerm('');
    } catch {
      setErrorKey('admin.tracks.failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleSurah = (n: number) => setEditing((prev) => (prev ? {
    ...prev,
    surahs: prev.surahs.includes(n)
      ? prev.surahs.filter((x) => x !== n)
      : [...prev.surahs, n].sort((a, b) => a - b),
  } : prev));

  const empty = tracks.length === 0;
  const newButton = (
    <button
      type="button"
      data-testid="new-track"
      onClick={() => { setEditing({ name: '', surahs: [] }); setErrorKey(null); }}
      className={cn(pill.base, pill.md, pill.primary)}
    >
      <Plus className="h-5 w-5" />
      {t('admin.tracks.new')}
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      {/* One main action, «أنشئ مساراً» — in the empty state while there is
          none (board a7). */}
      <PageHeader
        title={t('admin.tracks.title')}
        subtitle={t('admin.tracks.subtitle')}
        actions={!editing && !empty ? newButton : undefined}
      />

      {errorKey && <Notice tone="danger" testId="tracks-error">{t(errorKey)}</Notice>}

      {editing && (
        <SoftCard testId="track-editor" className="space-y-5">
          <label className="block max-w-xl space-y-2">
            <span className="text-sm font-medium text-ink">{t('admin.tracks.name')}</span>
            <input
              data-testid="track-name-input"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder={t('admin.tracks.namePlaceholder')}
              className={field}
            />
          </label>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-ink">
                {t('admin.tracks.surahs')}
                {' · '}
                <span data-testid="chosen-count" className="text-helper">
                  {count(editing.surahs.length)}
                </span>
              </p>
              <label className="relative w-full max-w-sm">
                <span className="sr-only">{t('admin.tracks.pick')}</span>
                <Search className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-helper" />
                <input
                  data-testid="surah-search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t('admin.tracks.pick')}
                  className={cn(field, 'rounded-full ps-12')}
                />
              </label>
            </div>
            {/* Chosen in lime, the redesign's "selected". */}
            <ul className="grid max-h-80 grid-cols-2 gap-2 overflow-y-auto rounded-[18px] bg-surface/60 p-2 sm:grid-cols-3 lg:grid-cols-4">
              {matches.map((meta) => {
                const chosen = editing.surahs.includes(meta.n);
                return (
                  <li key={meta.n}>
                    <button
                      type="button"
                      data-testid={`surah-${meta.n}`}
                      aria-pressed={chosen}
                      onClick={() => toggleSurah(meta.n)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-full px-4 py-2 text-start text-sm transition-colors',
                        chosen ? 'bg-lime text-accent-foreground' : 'bg-card text-ink hover:bg-tint',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-1.5 truncate">
                        {chosen && <Check className="h-3.5 w-3.5 shrink-0" />}
                        {language === 'ar' ? meta.name : meta.englishName}
                      </span>
                      <span className={cn('shrink-0 text-xs', chosen ? 'text-accent-foreground' : 'text-helper')}>
                        {count(meta.n)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-hair pt-4">
            <button
              type="button"
              data-testid="save-track"
              onClick={() => { void save(); }}
              disabled={busy}
              aria-busy={busy}
              className={cn(pill.base, pill.md, pill.primary, 'min-w-[140px]')}
            >
              {t('admin.tracks.save')}
            </button>
            <button
              type="button"
              data-testid="cancel-track"
              onClick={() => { setEditing(null); setErrorKey(null); }}
              className={cn(pill.base, pill.md, pill.quiet)}
            >
              {t('admin.tracks.cancel')}
            </button>
          </div>
        </SoftCard>
      )}

      {empty && !editing ? (
        <SoftCard as="div">
          <EmptyState
            testId="no-tracks"
            icon={<Route />}
            title={t('admin.tracks.emptyTitle')}
            body={t('admin.tracks.emptyBody')}
            action={newButton}
          />
        </SoftCard>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tracks.map((track) => (
            <li
              key={track.id}
              data-testid={`track-${track.id}`}
              className="card-soft flex items-center gap-3 p-5"
            >
              <IconTile><Route /></IconTile>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">{track.name}</p>
                <p className="text-sm text-helper">
                  {t('tracks.surahCount', { count: track.surahs.length, n: count(track.surahs.length) })}
                  {' · '}
                  {t('tracks.ayahCount', { count: track.totalAyahs, n: count(track.totalAyahs) })}
                </p>
              </div>
              <button
                type="button"
                data-testid={`edit-${track.id}`}
                onClick={() => {
                  setEditing({ id: track.id, name: track.name, surahs: track.surahs });
                  setErrorKey(null);
                }}
                className={cn(pill.base, pill.sm, pill.lime)}
              >
                <Pencil className="h-4 w-4" />
                {t('admin.tracks.edit')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
